import { supabase } from './supabaseClient';

export interface InvoiceItem {
  id?: string;
  invoice_id: string;
  article_id?: string;
  reference?: string;
  designation?: string;
  quantity: number;
  unit_price: number;
  total: number;
  vat_rate: number;
  created_at?: string;
  updated_at?: string;
}

export interface InvoicePayment {
  id?: string;
  invoice_id: string;
  amount: number;
  payment_date: string;
  payment_method?: string;
  notes?: string;
  created_at?: string;
}

export interface InvoiceDetail {
  id: string;
  client_id: string;
  store_id?: string; // ID du point de vente
  invoice_number: string;
  invoice_type: string;
  status: string;
  invoice_date: string;
  due_date?: string;
  total_htva: number;
  total_tva: number;
  total_ttc: number;
  total_acomptes: number;
  solde: number;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface InvoiceWithClient extends InvoiceDetail {
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  client_address?: string;
  client_postal_code?: string;
  client_city?: string;
  client_country?: string;
  client_vat_number?: string;
  store_name?: string;
  store_initiales?: string;
}

export interface InvoiceItemDetail extends InvoiceItem {
  article_name?: string;
  article_code?: string;
  article_reference?: string;
  article_description?: string;
}

export interface PendingPeppolInvoice {
  id: string;
  invoice_number: string;
  invoice_date: string;
  order_number: string;
  client: {
    name: string;
    email: string;
    address: string;
    postal_code: string;
    city: string;
    country: string;
    vat_number: string;
  };
  payments: Array<{
    method: string;
    amount: number;
    payment_date: string;
  }>;
  items: Array<{
    id: string;
    designation: string;
    quantity: number;
    unit_price: number;
    total: number;
  }>;
}

export const invoiceService = {
  async getInvoices({ search }: { search: string }) {
    let query = supabase
      .from('invoices')
      .select(
        'id, invoice_number, client_id, invoice_date, total_ttc, status, clients:client_id (full_name)'
      )
      .order('invoice_date', { ascending: false });
    if (search) {
      query = query.ilike('invoice_number', `%${search}%`);
    }
    const { data, error } = await query;
    if (error) throw error;
    // Map client name
    return data.map((inv: any) => ({
      ...inv,
      client_name: inv.clients?.full_name || 'N/A',
    }));
  },

  async getInvoicesPaginated(
    page: number,
    limit: number,
    searchTerm: string = '',
    storeFilter?: string
  ) {
    // Calculer l'offset basé sur la page et la limite
    const offset = (page - 1) * limit;

    // Construire la requête de base
    let query = supabase.from('invoices').select(
      `
        id, invoice_number, client_id, invoice_date, total_ttc, status,
        total_htva, total_tva, total_acomptes, solde, invoice_type, due_date, store_id,
        clients:client_id (full_name, email, phone, address, postal_code, city, country, vat_number),
        stores:store_id (name, initiales)
      `,
      { count: 'exact' }
    );

    // Filtre par point de vente si spécifié
    if (storeFilter) {
      query = query.eq('store_id', storeFilter);
    }

    // Ajouter la recherche si un terme est fourni
    if (searchTerm) {
      query = query.or(`
        invoice_number.ilike.%${searchTerm}%,
        clients.full_name.ilike.%${searchTerm}%
      `);
    }

    // Ajouter pagination - trier par date de facture (plus récente en premier), puis par date de création si même date
    query = query
      .order('invoice_date', { ascending: false })
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    // Exécuter la requête
    const { data, error, count } = await query;

    if (error) throw error;

    // Transformer les données pour inclure le nom du client et du magasin directement
    const invoices = data.map((invoice: any) => ({
      ...invoice,
      client_name: invoice.clients?.full_name || 'N/A',
      client_email: invoice.clients?.email || '',
      client_phone: invoice.clients?.phone || '',
      client_address: invoice.clients?.address || '',
      client_postal_code: invoice.clients?.postal_code || '',
      client_city: invoice.clients?.city || '',
      client_country: invoice.clients?.country || '',
      client_vat_number: invoice.clients?.vat_number || '',
      store_name: invoice.stores?.name,
      store_initiales: invoice.stores?.initiales,
    }));

    return {
      invoices,
      totalCount: count || 0,
      page,
      limit,
    };
  },

  async createInvoice(invoiceData: any) {
    const { data, error } = await supabase.from('invoices').insert([invoiceData]).select().single();
    if (error) throw error;
    return data;
  },

  async updateInvoice(id: string, invoiceData: any) {
    const { data, error } = await supabase
      .from('invoices')
      .update(invoiceData)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async deleteInvoice(id: string) {
    try {
      console.log('🗑️ Suppression transactionnelle de la facture:', id);

      const { error } = await supabase.rpc('delete_invoice', {
        p_invoice_id: id,
      });

      if (error) {
        console.error('❌ Erreur lors de la suppression de la facture:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('❌ Erreur dans deleteInvoice:', error);
      throw error;
    }
  },

  async getInvoiceById(id: string) {
    const { data, error } = await supabase.from('invoices').select('*').eq('id', id).single();
    if (error) throw error;
    return data;
  },

  async getInvoiceItems(invoiceId: string) {
    const { data, error } = await supabase
      .from('invoice_items')
      .select(
        `
        *,
        articles:article_id (
          code,
          name,
          description
        )
      `
      )
      .eq('invoice_id', invoiceId);
    if (error) throw error;

    // Transformer les données pour aplatir la structure
    const transformedData = data?.map((item: any) => ({
      ...item,
      article_code: item.code || item.articles?.code || '', // Priorité au code stocké directement
      article_name: item.designation || item.articles?.name || '',
      article_reference: item.reference || '',
      article_description: item.articles?.description || '',
    }));

    return transformedData;
  },

  async addInvoiceItem(itemData: Partial<InvoiceItemDetail>) {
    const { data, error } = await supabase
      .from('invoice_items')
      .insert([itemData])
      .select()
      .single();
    if (error) throw error;

    // Mettre à jour les totaux de la facture
    await this.updateInvoiceTotals(itemData.invoice_id as string);
    return data;
  },

  async updateInvoiceItem(id: string, itemData: Partial<InvoiceItemDetail>) {
    const { data, error } = await supabase
      .from('invoice_items')
      .update(itemData)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;

    // Mettre à jour les totaux de la facture
    if (data && data.invoice_id) {
      await this.updateInvoiceTotals(data.invoice_id);
    }
    return data;
  },

  async deleteInvoiceItem(id: string) {
    // D'abord, récupérer l'invoice_id pour pouvoir mettre à jour les totaux après
    const { data: item } = await supabase
      .from('invoice_items')
      .select('invoice_id')
      .eq('id', id)
      .single();

    const { error } = await supabase.from('invoice_items').delete().eq('id', id);
    if (error) throw error;

    // Mettre à jour les totaux de la facture
    if (item && item.invoice_id) {
      await this.updateInvoiceTotals(item.invoice_id);
    }
    return true;
  },

  async updateInvoiceTotals(invoiceId: string) {
    const { error } = await supabase.rpc('recalculate_invoice_totals', {
      p_invoice_id: invoiceId,
    });

    if (error) {
      console.error('Erreur lors du recalcul des totaux facture:', error);
    }

    return !error;
  },

  async getInvoicePayments(invoiceId: string) {
    const { data, error } = await supabase
      .from('invoice_payments')
      .select('*')
      .eq('invoice_id', invoiceId)
      .order('payment_date', { ascending: false });
    if (error) throw error;
    return data;
  },

  async addInvoicePayment(paymentData: Partial<InvoicePayment>) {
    const { data, error } = await supabase
      .from('invoice_payments')
      .insert([paymentData])
      .select()
      .single();
    if (error) throw error;

    // Mettre à jour les totaux de la facture
    if (paymentData.invoice_id) {
      await this.updateInvoiceTotals(paymentData.invoice_id);
    }
    return data;
  },

  async deleteInvoicePayment(id: string) {
    // D'abord, récupérer l'invoice_id pour pouvoir mettre à jour les totaux après
    const { data: payment } = await supabase
      .from('invoice_payments')
      .select('invoice_id')
      .eq('id', id)
      .single();

    const { error } = await supabase.from('invoice_payments').delete().eq('id', id);
    if (error) throw error;

    // Mettre à jour les totaux de la facture
    if (payment && payment.invoice_id) {
      await this.updateInvoiceTotals(payment.invoice_id);
    }
    return true;
  },

  async getTotalPaymentsByInvoiceId(invoiceId: string): Promise<number> {
    const { data, error } = await supabase
      .from('invoice_payments')
      .select('amount')
      .eq('invoice_id', invoiceId);

    if (error) throw error;
    return data ? data.reduce((sum, payment) => sum + (payment.amount || 0), 0) : 0;
  },

  /**
   * Générer un numéro de facture unique avec préfixe du magasin
   * Format: Préfixe + Année (2 chiffres) + ID (5 chiffres)
   * Exemple: FAL2500001 (FAL = préfixe Liège, 25 = année 2025, 00001 = ID)
   */
  async getNextInvoiceNumber(storeId?: string): Promise<string> {
    const today = new Date();
    const year = String(today.getFullYear()).slice(-2); // 2 derniers chiffres de l'année

    // Récupérer le préfixe du magasin
    let prefix = 'FA'; // Préfixe par défaut si pas de magasin

    if (storeId) {
      const { data: storeData, error: storeError } = await supabase
        .from('stores')
        .select('invoice_prefix')
        .eq('id', storeId)
        .single();

      if (!storeError && storeData?.invoice_prefix) {
        prefix = storeData.invoice_prefix;
      }
    }

    // Récupérer le dernier ID pour ce préfixe et cette année
    const pattern = `${prefix}${year}%`;
    const { data, error } = await supabase
      .from('invoices')
      .select('invoice_number')
      .like('invoice_number', pattern)
      .order('invoice_number', { ascending: false })
      .limit(1);

    if (error) throw error;

    let nextId = 1;
    if (data && data.length > 0) {
      const lastNumber = data[0].invoice_number;
      // Extraire les 5 derniers chiffres (l'ID)
      const lastId = parseInt(lastNumber.slice(-5));
      nextId = lastId + 1;
    }

    // Format: Préfixe + Année (2 chiffres) + ID (5 chiffres)
    return `${prefix}${year}${String(nextId).padStart(5, '0')}`;
  },

  /**
   * Vérifier si une facture existe déjà pour une commande
   */
  async getInvoiceByOrderId(orderId: string): Promise<InvoiceDetail | null> {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('order_id', orderId)
        .maybeSingle(); // Utiliser maybeSingle() au lieu de single() pour éviter les erreurs si aucune ligne

      if (error) {
        // Erreur 406 Not Acceptable - problème RLS
        if (
          error.code === 'PGRST301' ||
          error.message?.includes('406') ||
          error.message?.includes('Not Acceptable')
        ) {
          console.warn(
            '⚠️ Erreur RLS (406) lors de la recherche de facture par order_id. Vérifiez les politiques RLS.'
          );
          // Essayer une requête alternative avec store_id si disponible
          return null;
        }
        // Pas de facture trouvée (code normal)
        if (error.code === 'PGRST116') {
          return null;
        }
        // Colonne inexistante
        if (error.code === '42703' || error.message?.includes('order_id')) {
          return null;
        }
        throw error;
      }

      return data;
    } catch (error: any) {
      // Gérer les erreurs RLS
      if (
        error?.code === 'PGRST301' ||
        error?.message?.includes('406') ||
        error?.message?.includes('Not Acceptable')
      ) {
        return null;
      }
      // Gérer les erreurs de colonne inexistante
      if (error?.code === '42703' || error?.message?.includes('order_id')) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Créer une facture complète à partir d'une commande
   * - Crée la facture dans invoices
   * - Crée les items dans invoice_items
   * - Crée les paiements dans invoice_payments
   * - Met à jour le client si nécessaire
   */
  async createInvoiceFromOrder(params: {
    orderId: string;
    clientId: string;
    clientData: {
      nom: string;
      email?: string;
      telephone?: string;
      adresse: string;
      code_postal: string;
      ville: string;
      pays: string;
      numero_tva?: string;
    };
    storeId?: string;
    articles: Array<{
      id: string;
      code?: string;
      reference?: string;
      article: string;
      description?: string;
      quantite: number;
      prix_unitaire: number;
      tva_percent: number;
      prix_total: number;
    }>;
    payments: Array<{
      amount: number;
      payment_date: string;
      payment_method: string;
    }>;
    totals: {
      htva: number;
      tva: number;
      ttc: number;
      totalAcomptes: number;
      solde: number;
    };
  }): Promise<InvoiceDetail> {
    const { orderId, clientId, clientData, storeId, articles, payments, totals } = params;

    // 1. Mettre à jour le client si nécessaire
    const { error: clientUpdateError } = await supabase
      .from('clients')
      .update({
        full_name: clientData.nom,
        email: clientData.email || null,
        phone: clientData.telephone || null,
        address: clientData.adresse,
        postal_code: clientData.code_postal,
        city: clientData.ville,
        country: clientData.pays,
        vat_number: clientData.numero_tva || null,
      })
      .eq('id', clientId);

    if (clientUpdateError) {
      console.error('Erreur lors de la mise à jour du client:', clientUpdateError);
      throw clientUpdateError;
    }

    // 2. Générer le numéro de facture
    const invoiceNumber = await this.getNextInvoiceNumber(storeId);

    // 3. Créer la facture
    // Construire l'objet invoiceData en excluant les champs null/undefined pour éviter les erreurs de cache
    const invoiceData: any = {
      order_id: orderId,
      client_id: clientId,
      invoice_number: invoiceNumber,
      invoice_type: 'FACTURE',
      status: 'à établir',
      invoice_date: new Date().toISOString().split('T')[0],
      total_htva: totals.htva,
      total_tva: totals.tva,
      total_ttc: totals.ttc,
      total_acomptes: totals.totalAcomptes,
      solde: totals.solde,
    };

    // Ajouter store_id seulement s'il existe
    if (storeId) {
      invoiceData.store_id = storeId;
    }

    // Insérer la facture sans spécifier de colonnes dans le SELECT pour éviter les problèmes de cache
    const { data: invoice, error: invoiceError } = await supabase
      .from('invoices')
      .insert([invoiceData])
      .select(
        'id, order_id, client_id, invoice_number, invoice_type, status, invoice_date, total_htva, total_tva, total_ttc, total_acomptes, solde, store_id, created_at, updated_at'
      )
      .single();

    if (invoiceError) {
      console.error('Erreur lors de la création de la facture:', invoiceError);
      throw invoiceError;
    }

    // 4. Créer les items de facture
    const invoiceItems = articles.map(article => ({
      invoice_id: invoice.id,
      article_id: null, // On ne lie pas forcément à un article de la table articles
      reference: article.code || article.reference || '',
      designation: article.article,
      quantity: article.quantite,
      unit_price: article.prix_unitaire,
      total: article.prix_total,
      vat_rate: article.tva_percent,
    }));

    const { error: itemsError } = await supabase.from('invoice_items').insert(invoiceItems);

    if (itemsError) {
      console.error('Erreur lors de la création des items de facture:', itemsError);
      // Supprimer la facture créée en cas d'erreur
      await supabase.from('invoices').delete().eq('id', invoice.id);
      throw itemsError;
    }

    // 5. Créer les paiements de facture
    if (payments.length > 0) {
      const invoicePayments = payments.map(payment => ({
        invoice_id: invoice.id,
        amount: payment.amount,
        payment_date: payment.payment_date,
        payment_method: payment.payment_method,
      }));

      const { error: paymentsError } = await supabase
        .from('invoice_payments')
        .insert(invoicePayments);

      if (paymentsError) {
        console.error('Erreur lors de la création des paiements de facture:', paymentsError);
        // Supprimer la facture et les items créés en cas d'erreur
        await supabase.from('invoice_items').delete().eq('invoice_id', invoice.id);
        await supabase.from('invoices').delete().eq('id', invoice.id);
        throw paymentsError;
      }
    }

    // 6. Créer un rappel "à envoyer" pour l'utilisateur cible (in-app uniquement)
    // via RPC SECURITY DEFINER — le destinataire est codé en dur côté SQL.
    const { error: reminderError } = await supabase.rpc('create_invoice_reminder', {
      p_title: `Envoyer facture ${invoiceNumber} à ${clientData.nom}`,
      p_reminder_datetime: new Date().toISOString(),
      p_invoice_id: invoice.id,
    });
    if (reminderError) {
      console.error('Erreur lors de la création du rappel de facture:', reminderError);
    }

    return invoice;
  },

  /**
   * Factures encore "à établir" (à créer manuellement via Peppol/Billit),
   * avec infos client et lignes de produits.
   */
  async getPendingPeppolInvoices(): Promise<PendingPeppolInvoice[]> {
    const { data: invoices, error } = await supabase
      .from('invoices')
      .select(
        `
        id, invoice_number, invoice_date, order_id,
        clients:client_id (full_name, email, address, postal_code, city, country, vat_number),
        orders:order_id (order_number)
      `
      )
      .eq('status', 'à établir')
      .order('invoice_date', { ascending: true });

    if (error) throw error;
    if (!invoices || invoices.length === 0) return [];

    const invoiceIds = invoices.map((inv: any) => inv.id);
    const [{ data: items, error: itemsError }, { data: payments, error: paymentsError }] =
      await Promise.all([
        supabase
          .from('invoice_items')
          .select('id, invoice_id, designation, quantity, unit_price, total')
          .in('invoice_id', invoiceIds),
        supabase
          .from('invoice_payments')
          .select('invoice_id, payment_method, amount, payment_date')
          .in('invoice_id', invoiceIds),
      ]);

    if (itemsError) throw itemsError;
    if (paymentsError) throw paymentsError;

    return invoices.map((inv: any) => ({
      id: inv.id,
      invoice_number: inv.invoice_number,
      invoice_date: inv.invoice_date,
      order_number: inv.orders?.order_number || '',
      client: {
        name: inv.clients?.full_name || 'N/A',
        email: inv.clients?.email || '',
        address: inv.clients?.address || '',
        postal_code: inv.clients?.postal_code || '',
        city: inv.clients?.city || '',
        country: inv.clients?.country || '',
        vat_number: inv.clients?.vat_number || '',
      },
      payments: (payments || [])
        .filter((p: any) => p.invoice_id === inv.id)
        .map((p: any) => ({
          method: p.payment_method || '',
          amount: p.amount,
          payment_date: p.payment_date,
        })),
      items: (items || [])
        .filter((item: any) => item.invoice_id === inv.id)
        .map((item: any) => ({
          id: item.id,
          designation: item.designation || '',
          quantity: item.quantity,
          unit_price: item.unit_price,
          total: item.total,
        })),
    }));
  },

  /**
   * Marque une facture "à établir" comme facturée manuellement via Billit/Peppol.
   * Clôt aussi le rappel interne lié s'il existe.
   */
  async markInvoiceBilledExternally(invoiceId: string): Promise<void> {
    const { error } = await supabase
      .from('invoices')
      .update({ status: 'envoyée' })
      .eq('id', invoiceId);
    if (error) throw error;

    const { error: taskError } = await supabase
      .from('agenda_tasks')
      .update({ completed_at: new Date().toISOString() })
      .eq('invoice_id', invoiceId)
      .is('completed_at', null);
    if (taskError) {
      console.error('Erreur lors de la clôture du rappel lié:', taskError);
    }
  },
};
